/**
 * video_pipeline.status values, mirroring the backend.
 *
 * Source of truth is QT-MediaLibrary-BE-Trigger/utils/mediaStatus.js — these
 * strings are compared literally against what the API returns as videoStatus,
 * so the two must stay in step.
 *
 * THERE IS DELIBERATELY NO "Failed" STATUS. A pipeline failure is recorded in
 * separate columns (failedStage / failureReason) and the row keeps whatever
 * status it had. The reason is that every automated repair path on the backend
 * selects on status='Processed' — moving a broken video off that status would
 * make it unfixable by the tools built to fix it, while not hiding it from
 * users, since search filters on no status at all.
 *
 * So: status answers "where is it in the pipeline", and the failure columns
 * answer "did anything go wrong". Use hasPipelineFailure() for the latter.
 */
export const MEDIA_STATUS = {
  OPEN: "Open",
  PROCESSING: "Processing",
  PROCESSED: "Processed",
};

/**
 * States the pipeline will not move on from without new input — used to decide
 * when to stop polling.
 */
export const TERMINAL_STATUSES = [MEDIA_STATUS.PROCESSED];

export const isTerminalStatus = (status) => TERMINAL_STATUSES.includes(status);

/** True while the pipeline is actively working on the item. */
export const isInFlight = (status) =>
  status === MEDIA_STATUS.PROCESSING || status === MEDIA_STATUS.OPEN;

/** Badge modifier class for a given status. */
export const statusBadgeClass = (status) => {
  switch (status) {
    case MEDIA_STATUS.PROCESSING:
      return "status-badge--processing";
    case MEDIA_STATUS.OPEN:
      return "status-badge--pending";
    case MEDIA_STATUS.PROCESSED:
      return "status-badge--processed";
    default:
      return "status-badge--neutral";
  }
};

/**
 * Human-facing label. Named by what the user recognises, not by the DB value —
 * "Open" means nothing to an admin looking at a media list.
 */
export const statusLabel = (status) => {
  switch (status) {
    case MEDIA_STATUS.PROCESSING:
      return "Processing…";
    case MEDIA_STATUS.OPEN:
      return "Pending";
    default:
      return status;
  }
};

/** Which pipeline stage failed, when one did. Mirrors STAGE on the backend. */
export const FAILED_STAGE = {
  THUMBNAIL: "thumbnail",
  TAGS: "tags",
  TRANSCODE: "transcode",
  VARIANTS: "variants",
};

/** Does the row actually have playable renditions right now? */
export const hasPlayableVersions = (row) =>
  Array.isArray(row?.qualityTypeArray) && row.qualityTypeArray.length > 0;

/**
 * True when a recorded failure is CONTRADICTED by the row's own data.
 *
 * failed_stage records what happened at the time. It does not necessarily
 * describe the video now: a repaired video can keep a stale flag, because not
 * every repair path clears it (scripts/reTranscodeJob.js did not). Trusting the
 * flag alone produced a badge reading "Will not play — no video versions were
 * produced" on a video with four working renditions, sitting next to a play
 * button that worked. A claim the surrounding UI immediately disproves is worse
 * than no claim: it teaches people to disbelieve the badge.
 *
 * So the consequence is derived from observable state, using data the media list
 * already carries. There is no equivalent check for TAGS — nothing in the list
 * response reveals whether tags exist — so a tags failure is still taken at face
 * value.
 */
export const isStaleFailure = (row) => {
  if (!row || !row.failedStage) return false;
  switch (row.failedStage) {
    case FAILED_STAGE.VARIANTS:
    case FAILED_STAGE.TRANSCODE:
      return hasPlayableVersions(row);
    case FAILED_STAGE.THUMBNAIL:
      return Boolean(row.thumbnail_image);
    default:
      return false;
  }
};

/**
 * True when the row carries a recorded pipeline failure that its own data does
 * not contradict. Everything downstream (summary, tooltip, badge) keys off this,
 * so a repaired video silently stops claiming to be broken.
 */
export const hasPipelineFailure = (row) =>
  Boolean(row && row.failedStage) && !isStaleFailure(row);

/**
 * True when the row has sat in Processing far longer than ingestion should take.
 *
 * This catches what hasPipelineFailure CANNOT. A recorded failure requires
 * something to have recorded it — but the worst ingestion breaks record nothing
 * at all (the queue accepts work no worker ever performs, so the code that
 * writes failedStage never runs). Those rows just sit at 'Processing', looking
 * exactly like a healthy in-progress video.
 *
 * The backend decides the threshold and sends 1/0, so the cut-off lives in one
 * place. Deliberately NOT recomputed here from a timestamp: two copies of the
 * rule drift, and the UI copy below stays true whatever the backend picks.
 */
export const isStuckProcessing = (row) => Boolean(row && Number(row.isStuck) === 1);

/** Admin-facing wording. No duration named here — the backend owns the number. */
export const stuckSummary = (row) =>
  isStuckProcessing(row) ? "Stalled — still processing long after upload" : null;

/** Tooltip: what to actually do about it. */
export const stuckDetail = (row) =>
  isStuckProcessing(row)
    ? "This video has been Processing far longer than expected. Renditions or tagging did not finish and nothing was recorded — use Retry on the Reprocess page (it re-runs the stage that did not complete)."
    : undefined;

/**
 * What the failure means for the person reading the list, in their terms — not
 * the pipeline's. An admin cares that a video will not play, not that a
 * transcoder job returned non-zero.
 */
export const failureSummary = (row) => {
  if (!hasPipelineFailure(row)) return null;

  switch (row.failedStage) {
    case FAILED_STAGE.VARIANTS:
    case FAILED_STAGE.TRANSCODE:
      return "Will not play — no video versions were produced";
    case FAILED_STAGE.TAGS:
      return "Not searchable — tagging did not complete";
    case FAILED_STAGE.THUMBNAIL:
      return "No preview image";
    default:
      return "Processing did not complete";
  }
};

/** The raw reason, for the tooltip — useful to whoever has to fix it. */
export const failureDetail = (row) => {
  if (!hasPipelineFailure(row)) return undefined;
  const reason = row.failureReason ? ` (${row.failureReason})` : "";
  return `${failureSummary(row)}${reason}`;
};

/**
 * Derived media state for DISPLAY — NOT a DB column, and it does NOT change what
 * `status` means. `status` stays the coarse lifecycle marker the backend repair
 * tools depend on ('Processed' = ingested + tagged). This answers the honest
 * question "is the pipeline actually complete?" so the media list can stop
 * showing "Processed" on a video whose pipeline did not finish.
 *
 * Keyed on OBSERVED STATE (does it have playable renditions?), so it is correct
 * even for the historical no-variant rows that predate failed_stage / migration
 * 0007 — the same reason the No-Variants repair tab works pre-0007.
 *   inflight    — Open / Processing (still working)
 *   incomplete  — Processed, but a required stage is missing: a non-stale recorded
 *                 failure (of ANY stage, including a recorded 'thumbnail' failure —
 *                 hasPipelineFailure does not exempt it), OR a video with no playable
 *                 versions (observed). Note the asymmetry: a merely-missing thumbnail
 *                 with no recorded failure is cosmetic and does NOT flip this (there
 *                 is no `!thumbnail_image` branch below) — only a RECORDED thumbnail
 *                 failure does. mediaListStatus() reflects the same split in its
 *                 labels ('No thumbnail' neutral vs. amber when a failure is recorded).
 *   ready       — Processed and complete (or an image, which has no variants).
 */
export const MEDIA_STATE = { READY: "ready", INCOMPLETE: "incomplete", INFLIGHT: "inflight" };

const isVideoRow = (row) => Number(row?.fileTypeId) === 2;

export const mediaState = (row) => {
  if (!row) return MEDIA_STATE.READY;
  if (isInFlight(row.videoStatus)) return MEDIA_STATE.INFLIGHT;
  if (row.videoStatus !== MEDIA_STATUS.PROCESSED) return MEDIA_STATE.READY;
  if (hasPipelineFailure(row)) return MEDIA_STATE.INCOMPLETE;                    // recorded, non-stale
  if (isVideoRow(row) && !hasPlayableVersions(row)) return MEDIA_STATE.INCOMPLETE; // observed: no renditions
  return MEDIA_STATE.READY;
};

/**
 * The media-list status as a SINGLE pill that NAMES what failed — not a vague
 * "Incomplete"/"Stuck". Returns { label, className, title } where title is the
 * tooltip (consequence + reason), so nothing stacks beneath the pill. Ordered by
 * severity: a video that won't play matters more than a missing poster image.
 * Display-only — the DB status is untouched.
 */
export function mediaListStatus(row) {
  if (!row) return { label: "—", className: "status-badge--neutral", title: undefined };

  if (row.videoStatus === MEDIA_STATUS.OPEN)
    return { label: "Pending", className: "status-badge--pending", title: undefined };

  if (row.videoStatus === MEDIA_STATUS.PROCESSING) {
    // Since 2026-10-15 renditions are produced BEFORE tagging, so a row still in
    // Processing may have a RECORDED failure at the transcode stage (it stays
    // Processing; the stage-aware Retry rebuilds renditions). A recorded, non-stale
    // failure is the more specific fact and wins over the stuck flag.
    if (hasPipelineFailure(row))
      return { label: failureStageLabel(row), className: "status-badge--incomplete", title: failureDetail(row) };
    // Stuck past the backend threshold: which stage stalled is unknown here.
    if (isStuckProcessing(row))
      return { label: "Stalled", className: "status-badge--incomplete", title: stuckDetail(row) };
    return { label: "Processing…", className: "status-badge--processing", title: undefined };
  }

  // Processed: name the worst gap, most severe first.
  if (isVideoRow(row) && !hasPlayableVersions(row)) {         // no renditions → won't play
    // Only surface the recorded reason here when it is ABOUT transcoding/variants.
    // failedStage is one column holding the LAST failure — if it says 'tags' or
    // 'thumbnail', failureDetail() would describe a different stage and contradict
    // the "Transcode failed" label (pill says one thing, tooltip another).
    const transcodeReason =
      row.failedStage === FAILED_STAGE.TRANSCODE || row.failedStage === FAILED_STAGE.VARIANTS
        ? failureDetail(row)
        : null;
    return {
      label: "Transcode failed",
      className: "status-badge--incomplete",
      title: transcodeReason || "Will not play — no video versions were produced",
    };
  }
  if (hasPipelineFailure(row))                                 // a recorded failure the list can't observe (tags)
    return { label: failureStageLabel(row), className: "status-badge--incomplete", title: failureDetail(row) };
  if (!row.thumbnail_image)                                    // cosmetic: plays + searchable, just no poster
    return { label: "No thumbnail", className: "status-badge--neutral",
             title: "No preview image — the video still plays; fixable in the Missing Thumbnails tab" };
  return { label: "Processed", className: "status-badge--processed", title: undefined };
}

/** Short "what failed" label for a recorded failure stage. */
const failureStageLabel = (row) => {
  switch (row?.failedStage) {
    case FAILED_STAGE.TAGS: return "Tagging failed";
    case FAILED_STAGE.VARIANTS:
    case FAILED_STAGE.TRANSCODE: return "Transcode failed";
    case FAILED_STAGE.THUMBNAIL: return "No thumbnail";
    default: return "Incomplete";
  }
};

// Thin accessors so the JSX can stay simple (all three read the one derivation).
export const mediaStateLabel = (row) => mediaListStatus(row).label;
export const mediaStateBadgeClass = (row) => mediaListStatus(row).className;
export const mediaStateDetail = (row) => mediaListStatus(row).title;
